import { formatINR } from '../../../shared/utils/formatters';
import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { Commission } from '../types/commission.types'
import { SellerNameDisplay } from './SellerNameDisplay'

interface CommissionDetailsDrawerProps {
    open: boolean
    onClose: () => void
    commission: Commission | null
    onEdit: (commission: Commission) => void
    onDelete: (commission: Commission) => void
}

export const CommissionDetailsDrawer: React.FC<CommissionDetailsDrawerProps> = ({
    open,
    onClose,
    commission,
    onEdit,
    onDelete
}) => {
    if (!commission) return null

    return (
        <Drawer
            title="Commission Details"
            placement="right"
            width={600}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Commission Information" column={1} bordered>


                    <Descriptions.Item label="Scope">
                        <Tag color={
                            commission.scope === 'product' ? 'blue' :
                                commission.scope === 'category' ? 'purple' :
                                    commission.scope === 'seller' ? 'orange' :
                                     commission.scope === 'campaign' ? 'red' : 'default'
                        }>
                            {(commission.scope || 'product').toUpperCase()}
                        </Tag>
                    </Descriptions.Item>

                    {commission.scope === 'product' && (
                        <Descriptions.Item label="Products">
                            {commission.products && commission.products.length > 0 ? (
                                commission.products.length > 5 ? (
                                    <details>
                                        <summary>{commission.products.length} Products</summary>
                                        <ul style={{ paddingLeft: 20, margin: '5px 0' }}>
                                            {commission.products.map(p => (
                                                <li key={p.id}>{p.name}</li>
                                            ))}
                                        </ul>
                                    </details>
                                ) : (
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {commission.products.map(p => (
                                            <li key={p.id}>{p.name}</li>
                                        ))}
                                    </ul>
                                )
                            ) : (
                                <span style={{ color: 'gray' }}>No products selected</span>
                            )}
                        </Descriptions.Item>
                    )}

                    {commission.scope === 'category' && (
                        <Descriptions.Item label="Category">
                            <Tag color="cyan">{commission.category || 'All Categories'}</Tag>
                        </Descriptions.Item>
                    )}

                    {commission.scope === 'seller' && (
                        <Descriptions.Item label="Seller">
                            {commission.sellerId ? <SellerNameDisplay sellerId={commission.sellerId} /> : 'N/A'}
                        </Descriptions.Item>
                    )}


                    {commission.scope === 'campaign' && (
                        <Descriptions.Item label="Campaign ID">
                            {commission.campaignId || 'N/A'}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Commission Type">
                        <Tag color={commission.commissionType === 'percentage' ? 'green' : 'orange'}>
                            {commission.commissionType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Commission Value">
                        <strong style={{ fontSize: 18, color: '#1890ff' }}>
                            {commission.commissionValue != null
                                ? (commission.commissionType === 'percentage'
                                    ? `${commission.commissionValue}%`
                                    : formatINR(commission.commissionValue.toLocaleString()))
                                : 'N/A'}
                        </strong>
                    </Descriptions.Item>
                    {commission.minOrderValue != null && (
                        <Descriptions.Item label="Minimum Order Value">
                            {formatINR(commission.minOrderValue.toLocaleString())}
                        </Descriptions.Item>
                    )}
                    {commission.maxOrderValue != null && (
                        <Descriptions.Item label="Maximum Order Value">
                            {formatINR(commission.maxOrderValue.toLocaleString())}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Status">
                        <Tag color={commission.status === 'active' ? 'green' : 'gray'}>
                            {commission.status.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">{commission.createdAt}</Descriptions.Item>
                    <Descriptions.Item label="Last Updated">{commission.updatedAt}</Descriptions.Item>
                </Descriptions>

                <Divider />

                <Space>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => onEdit(commission)}>
                        Edit Commission
                    </Button>
                    <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(commission)}>
                        Delete Commission
                    </Button>
                </Space>
            </Space>
        </Drawer>
    )
}
